/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
} from 'react';
import PropTypes from 'prop-types';

import OnTraccrNumberInput from '../../../../common/inputs/OnTraccrNumberInput';

const DEBOUNCE_MS = 50;

function TimeTrackingGridHourInput({ value, onChange, disabled }) {
  const committed = Math.max(0, value);
  const [local, setLocal] = useState(committed);
  const focusedRef = useRef(false);
  const debounceTimerRef = useRef(null);
  const localRef = useRef(committed);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const flushParent = useCallback((next) => {
    localRef.current = next;
    onChange(next);
  }, [onChange]);

  const scheduleParent = useCallback((next) => {
    localRef.current = next;
    setLocal(next);
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onChange(next);
    }, DEBOUNCE_MS);
  }, [onChange, clearDebounce]);

  useEffect(() => {
    if (focusedRef.current) return;
    setLocal(committed);
    localRef.current = committed;
  }, [committed]);

  useEffect(() => () => clearDebounce(), [clearDebounce]);

  const onFocus = useCallback(() => {
    focusedRef.current = true;
  }, []);

  const onBlur = useCallback(() => {
    focusedRef.current = false;
    clearDebounce();
    flushParent(localRef.current);
  }, [clearDebounce, flushParent]);

  const onNumberChange = useCallback((v) => {
    scheduleParent(v);
  }, [scheduleParent]);

  const onPlusMinusPress = useCallback((delta) => {
    const next = Math.max(0, localRef.current + delta);
    localRef.current = next;
    setLocal(next);
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      onChange(next);
    }, DEBOUNCE_MS);
  }, [onChange, clearDebounce]);

  const isMinusDisabled = local <= 0 || disabled;

  return (
    <div className="time-tracking-grid-cell-number-input-container">
      <div
        className={`time-tracking-grid-cell-minus ${isMinusDisabled ? 'disabled' : ''}`}
        onClick={isMinusDisabled ? undefined : () => onPlusMinusPress(-1)}
      >
        -
      </div>
      <OnTraccrNumberInput
        value={local}
        disabled={disabled}
        min={0}
        precision={2}
        onChange={onNumberChange}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder="Hrs"
        className={`time-tracking-grid-cell-number-input ${disabled ? 'disabled' : ''}`}
      />
      <div
        className={`time-tracking-grid-cell-plus ${disabled ? 'disabled' : ''}`}
        onClick={disabled ? undefined : () => onPlusMinusPress(1)}
      >
        +
      </div>
    </div>
  );
}

TimeTrackingGridHourInput.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default memo(TimeTrackingGridHourInput);
